import { Review as TReview, Option, Image } from 'src/types';
import StarSvg from 'public/images/svg_icons/star.svg';
import Link from 'next/link';
import { AppRoutes } from 'src/lib/routes';
import dayjs from 'dayjs';
import ReadMore from 'src/components/ReadMore';
import useBreakpoints from 'src/hooks/useBreakpoints';
import ImageWithFallback from 'src/components/ImageWithFallback';
import { useLocale, useTranslations } from 'next-intl';
import { LOCALE_DATE_FORMAT } from 'src/lib/constants';

type Props = {
  review: TReview;
  onShowMore?: (review: TReview) => void;
  onClickImage?: (image: Image) => void;
  showOption?: boolean;
  option?: Option;
  mobileLength?: number;
  desktopLength?: number;
  readMoreClass?: string;
  hideDate?: boolean;
  isSSResponse?: boolean;
};

const MOBILE_LENGTH = 120;
const DESKTOP_LENGTH = 280;

const Review: React.FC<Props> = ({
  review,
  showOption,
  onShowMore,
  option,
  onClickImage,
  mobileLength = MOBILE_LENGTH,
  desktopLength = DESKTOP_LENGTH,
  readMoreClass,
  hideDate,
  isSSResponse
}) => {
  const locale = useLocale();
  const translation = useTranslations('components.Review')
  const rating = Math.round(review.rating || 3);
  const { isScreenMd } = useBreakpoints();
  const maxNumImg = isScreenMd ? 5 : 3;
  const maxLength = isScreenMd ? desktopLength : mobileLength;
  const images: Image[] = review.images;
  const UIMonthFormat = LOCALE_DATE_FORMAT[locale].UIMonthFormat

  return (
    <>
      <div className="review__item" id={`review-${review.id}`}>
        <div className="tw-text-brand-navy tw-font-semibold tw-mb-1 tw-leading-none">{review.reviewer}</div>
        {review.reviewDate && !hideDate && (
          <div className="tw-text-sm tw-mb-1">
            {dayjs(review.reviewDate).format(UIMonthFormat)}
          </div>
        )}
        <div>
          {[...Array(rating)].map((key) => (
            <StarSvg key={key} width={20} height={20} className="tw-text-orange tw-align-top" />
          ))}
        </div>

        {images.length > 0 ? (
          <div className="tw-relative tw-flex tw-items-center tw--mx-2 tw-mt-4">
            {images.slice(0, maxNumImg).map((image) => (
              <div
                key={image.id}
                className="tw-px-2 tw-cursor-pointer tw-flex-grow"
                style={{ flexBasis: '1%' }}
                onClick={() => {
                  if (typeof onClickImage === 'function') {
                    onClickImage(image);
                  }
                }}
              >
                <div className="tw-relative tw-w-full" style={{ paddingBottom: '100%' }}>
                  <ImageWithFallback
                    record={image}
                    imgField="webpSmUrl"
                    alt={image.caption || 'review image'}
                    fill
                    className="tw-object-cover tw-rounded-lg tw-w-full tw-h-full tw-border-2 tw-border-brand-green tw-border-solid"
                  />
                </div>
              </div>
            ))}
            {[...Array(images.length < maxNumImg ? maxNumImg - images.length : 0)].map(
              (_, index) => (
                <div key={index} className="tw-px-2 tw-flex-grow" style={{ flexBasis: '1%' }}>
                  <div className="tw-relative tw-w-full" style={{ paddingBottom: '100%' }}></div>
                </div>
              )
            )}
            {images.length > maxNumImg ? (
              <div
                className="tw-border tw-border-white tw-leading-none tw-bottom-2 tw-right-4 tw-absolute tw-rounded-lg tw-p-1 tw-text-white"
                style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}
              >
                {images.length - maxNumImg}+
              </div>
            ) : null}
          </div>
        ) : null}

        {review.body && review.strippedBody && (
          <div className="tw-mt-4">
            {!['en', 'default'].includes(locale) ? <span className="tw-font-semibold tw-text-sm tw-block">{translation('aiTranslated')}</span> : null}
            <ReadMore
              as="div"
              className="review__item-content tw-text-brand-navy tw-text-sm md:tw-text-base tw-cursor-pointer"
              mobileLength={mobileLength}
              desktopLength={desktopLength}
              fullContent={review.body}
              content={review.strippedBody}
              dotOnly={review.body.length > maxLength && onShowMore ? true : false}
              readMoreText={translation('showMore')}
              readMoreClass={readMoreClass}
            />
            {review.body.length > maxLength && onShowMore ? (
              <div
                className="tw-inline-block tw-font-semibold tw-underline tw-mt-2 tw-cursor-pointer"
                onClick={() => {
                  if (typeof onShowMore === 'function') onShowMore(review);
                }}
              >
                {translation('showMore')}
              </div>
            ) : null}
          </div>
        )}

        {review.option && showOption && (
          <div className="tw-mt-4">
            <span className="tw-text-brand-navy tw-mr-1 tw-font-normal tw-text-sm">{translation('booked')}:</span>
            {review.option.isPublished ? (
              <a href={AppRoutes.experienceRoute({ id: review.option.slugCode }).toPath()} className="tw-underline tw-font-normal tw-text-sm tw-text-brand-navy hover:tw-font-semibold">
                {review.option.name}
              </a>
            ) : (
              review.option.name
            )}
          </div>
        )}
      </div>

      {review.reply && !onShowMore && (
        <div className="review__item tw-mt-5">
          <div className="d-flex">
            <div className="tw-border-l-4 tw-border-purple tw-border-solid pr-3 pr-md-4"></div>
            <div>
              <div className="tw-font-semibold tw-mb-1">
                {isSSResponse ? `${translation('responseFrom')} Seek Sophie` : translation('hostResponse')}
              </div>
              <div className="tw-text-sm md:tw-text-base" dangerouslySetInnerHTML={{ __html: review.reply.message }}>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Review;
